import { TFunction } from "i18next";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { TermsVersionSummary } from "./types";

// Only the index is needed to name a version, so these two take the narrowest
// shape that carries it — the detail payload satisfies it as well as the list
// summary, which is what lets the version page reuse them instead of repeating
// the rule and its three copy keys.
type VersionNaming = Pick<TermsVersionSummary, "versionIndex">;

// What the entry is called. Index 0 is the terms the contract started with,
// which the design calls "Initial terms".
export const versionTitle = (version: VersionNaming, t: TFunction): string =>
  version.versionIndex === 0
    ? t("contracts.termsHistory.initialTerms")
    : t("contracts.termsHistory.amendment");

// The version marker, shown only where there is a sequence to place it in.
export const versionMarker = (
  version: VersionNaming,
  t: TFunction
): string | null =>
  version.versionIndex === 0
    ? null
    : t("contracts.termsHistory.versionLabel", { index: version.versionIndex });

// The full name a version is shown under: the title, plus its marker where it
// has one. The timeline renders the two separately, so both are still exported.
export const versionFullName = (version: VersionNaming, t: TFunction): string =>
  [versionTitle(version, t), versionMarker(version, t)]
    .filter(Boolean)
    .join(" ");

// The span a version was, or will be, in force. `effectiveUntil` is null for the
// terms currently in force — an open-ended span, not a missing one, so it reads
// "Ongoing" rather than collapsing to blank the way formatDateRange does when
// it is handed an empty end date.
export const versionPeriod = (
  version: TermsVersionSummary,
  t: TFunction
): string | null => {
  if (!version.effectiveDate) return null;
  const start = formatDateShort(version.effectiveDate);
  const end = version.effectiveUntil
    ? formatDateShort(version.effectiveUntil)
    : t("contracts.detailedView.ongoing");
  return `${start} - ${end}`;
};
